import type {
  FilterInterfaceFilter,
  FormattingFilter,
  GroupingRule,
  ViewIds,
} from '@ardoq/api-types';
import type { LoadedGraphWithViewpointMode } from '@ardoq/data-model';
import {
  buildGraphFromTraversalAndSearch,
  getAppliedGroups,
  getGraphInterfaceForEnhancedScopeData,
  getGraphNodeOrGroupLabelForViewpointMode,
  getLabelsInterfaceForEnhancedScopeData,
  getRepresentationInterfaceForEnhancedScopeData,
} from '@ardoq/graph';
import { groupingRuleOperations } from '@ardoq/core';
import type { FormatUserFieldFn } from '@ardoq/scope-data';
import type { I18nLocale } from '@ardoq/i18n';
import { TRUE_COLOR_OPTIONS } from 'tabview/consts';
import type { HierarchicalTreemapNode } from '../types';
import { makeReduceNodes, toRootItems } from './reduceNodes';

/**
 * Resolves `User` field values to a display name; they store a user id. Threaded in rather
 * than reached for, so the tree builder stays a pure function of its arguments.
 */
export type FormatUserField = FormatUserFieldFn;

/**
 * The label and conditional-formatting rules in force, in the one shape both the labels and
 * the representation interfaces accept.
 */
export type LabelFormattingFilter = FormattingFilter | FilterInterfaceFilter;

type ToTreemapNodesArgs = {
  loadedGraph: LoadedGraphWithViewpointMode;
  /**
   * The viewpoint's grouping rules. These views are a hierarchy of whatever the user grouped
   * by — the parent hierarchy is one such grouping, not the only one, and locking them to it
   * would make them the one pair of views in the product that ignores the grouping panel.
   */
  groupingRules: GroupingRule[];
  formattingFilters: LabelFormattingFilter[];
  formatUserFieldFn: FormatUserField;
  viewId: ViewIds;
  i18nLocale: I18nLocale;
};

/**
 * The tree the three hierarchical views lay out, built from a viewpoint's own scope data.
 *
 * Structurally the same as the workspace-mode path in `buildViewModel`: build a raw graph,
 * take its roots, reduce them into drawable nodes. What differs is only where the graph comes
 * from — a viewpoint has already been traversed, so this reads its result rather than running
 * a traversal from a context component.
 */
export const toTreemapNodes = ({
  loadedGraph,
  groupingRules,
  formattingFilters,
  formatUserFieldFn,
  viewId,
  i18nLocale,
}: ToTreemapNodesArgs): HierarchicalTreemapNode[] => {
  const { scopeData } = loadedGraph;
  const validGroupingRules = groupingRules.filter(
    groupingRuleOperations.isValidRule
  );
  /* A viewpoint saved without a grouping tab entry arrives here with none, and that is the
     ordinary case rather than an edge one. Passing it through would put every component at
     the root as a sibling — one flat level, no rollup, which is the thing these views exist
     to draw. Workspace mode falls back the same way. */
  const appliedGroupingRules = getAppliedGroups(validGroupingRules);

  const graphInterface = getGraphInterfaceForEnhancedScopeData({
    enhancedScopeData: scopeData,
    conditionalFormatting: [],
    groupingRules: appliedGroupingRules,
    i18nLocale,
  });

  const { rootGroups, componentMap } = buildGraphFromTraversalAndSearch({
    graphInterface,
    graph: scopeData.graphData,
    groupingRules: appliedGroupingRules,
    viewId,
    loadedGraph,
    i18nLocale,
  });

  const labelsInterface = {
    ...graphInterface,
    ...getLabelsInterfaceForEnhancedScopeData({
      enhancedScopeData: scopeData,
      formattingFilters,
      formatUserFieldFn,
      i18nLocale,
    }),
  };
  const representationInterface =
    getRepresentationInterfaceForEnhancedScopeData({
      enhancedScopeData: scopeData,
      formattingFilters,
    });

  const { nodes } = toRootItems({ rootGroups, componentMap }).reduce(
    makeReduceNodes({
      /* `mainLabel` alone: the sub- and other-labels are for renderers with room beneath a
         node's name, and a treemap tile has none. */
      resolveLabel: item =>
        getGraphNodeOrGroupLabelForViewpointMode(
          labelsInterface,
          item,
          i18nLocale
        ).mainLabel,
      resolveRepresentationData: modelId =>
        representationInterface.getRepresentationData(modelId),
      resolveColor: modelId =>
        representationInterface.getComponentDisplayColor(
          modelId,
          TRUE_COLOR_OPTIONS
        ).fill ?? null,
      isComponent: modelId => Boolean(scopeData.componentsById[modelId]),
    }),
    { nodes: [] }
  );
  return nodes;
};
